import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';

/**
 * Per-customer alert sound config — shape exposed on
 * /rest/api/brand-config.error_alert_sound (added in f2-admin-service
 * customer-directory.service builder).
 */
export interface ErrorAlertSound {
  enabled: boolean;
  instrument: string;
  note: string;
}

/**
 * Plays a short alert tone for new error reports. Different customers
 * pick different instrument + note pairs so a listen tells operators
 * WHICH customer is throwing errors without looking at the screen.
 *
 * Engine choice: Web Audio API direct synthesis (no soundfont samples,
 * no library). "instrument" maps to oscillator type + ADSR envelope —
 * the timbres are distinguishable (bells vs marimba vs organ) without
 * the bundle weight of a sampler. If we later want real sampled
 * instruments, swap _scheduleNote without touching the picker/contract.
 *
 * Per-customer config is fetched from brand-config and cached for 60s
 * (the same TTL the SPA already uses for other brand-config reads).
 */
@Injectable({ providedIn: 'root' })
export class SoundPlayerService {
  private _ctx: AudioContext | null = null;
  // Auto-release: after 5 seconds of silence the AudioContext is closed
  // so the browser doesn't leave the audio channel active + the OS
  // audio session marker (Chrome's "This tab is playing sound" ear,
  // some OSes' active-audio LED) clears. Next _playNote recreates a
  // fresh context. Handle is a setTimeout id.
  // Mike 2026-07-03: 'when it plays a sound after 5 seconds of silence
  // it needs to turn off / release sound mechanism so it doesn't leave
  // the audio channel active.'
  private _releaseTimer: any = null;
  private readonly AUTO_RELEASE_MS = 5000;
  // Cold-start warm-up delay for the FIRST note after AudioContext
  // creation (or after the auto-release path recreates it). The audio
  // instrument needs a moment to spin up — without this delay the
  // first tone routes to the destination before the OS audio session
  // is ready and is silently dropped. Subsequent notes on the same
  // warm context play immediately. Mike 2026-07-07 (loss of first
  // alert after 5s+ silence gap).
  private _warm: boolean = false;
  private readonly WARMUP_DELAY_S = 1.0;
  // Mute toggle persisted across reloads — operators may want to silence
  // the cabinet during a deploy storm without losing the visual feed.
  private _muted: boolean = (() => {
    try { return localStorage.getItem('error_reports_sound_muted') === '1'; } catch { return false; }
  })();
  // Per-operator volume 0..100, persisted in localStorage. Independent of
  // any per-customer config — every operator can set their own ear-comfort
  // level. Scales the envelope peak (env.peak * volume/100) at play time.
  // Default 70 = a noticeable but non-startling alert.
  private _volume: number = (() => {
    try {
      const raw = localStorage.getItem('error_reports_sound_volume');
      if (raw === null) return 70;
      const v = Number(raw);
      return Number.isFinite(v) && v >= 0 && v <= 100 ? v : 70;
    } catch { return 70; }
  })();
  // Per-customer config cache. slug → { config | null, expiresAt }.
  // null = brand-config returned no config (customer doc has no field) —
  // cache the null to avoid refetching on every event.
  private _cache: Map<string, { config: ErrorAlertSound | null; expiresAt: number }> = new Map();
  private readonly CACHE_TTL_MS = 60 * 1000;

  constructor(private http: HttpClient) {}

  /** Toggle mute. Persists to localStorage. */
  setMuted(muted: boolean): void {
    this._muted = muted;
    try { localStorage.setItem('error_reports_sound_muted', muted ? '1' : '0'); } catch { /* ignore */ }
  }
  isMuted(): boolean { return this._muted; }

  /** Set operator volume 0..100. Clamped + persisted. */
  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(100, Number(v) || 0));
    this._volume = clamped;
    try { localStorage.setItem('error_reports_sound_volume', String(clamped)); } catch { /* ignore */ }
  }
  getVolume(): number { return this._volume; }

  /**
   * Play the configured sound for a customer. Looks up brand-config
   * (cached), no-ops gracefully when disabled / no config / muted /
   * browser autoplay-blocked. Fire-and-forget — caller does not await.
   */
  async playForCustomer(customerSlug: string | null | undefined): Promise<void> {
    if (this._muted) return;
    // Reports without a customer fall back to the 'f2' company-doc tone —
    // configurable via the Sound Settings dialog as the "Default /
    // non-customer reports" row.
    const slug = (customerSlug && typeof customerSlug === 'string') ? customerSlug : 'f2';
    const cfg = await this._resolveConfig(slug);
    if (!cfg || !cfg.enabled) return;
    this._playNote(cfg.instrument, cfg.note);
  }

  /** Drop a single customer's cached config so the next playForCustomer
   *  call refetches from brand-config. Used by the Sound Settings dialog
   *  after a save so subsequent live events use the updated tone. */
  invalidateCustomer(slug: string): void {
    if (slug) this._cache.delete(slug);
  }

  /**
   * Preview a sound directly. Used by the admin picker's Preview button —
   * bypasses cache + brand-config and plays the supplied config. Uses
   * the operator's current local volume (same setting as live playback).
   */
  preview(instrument: string, note: string): void {
    this._playNote(instrument, note);
  }

  private async _resolveConfig(slug: string): Promise<ErrorAlertSound | null> {
    const now = Date.now();
    const hit = this._cache.get(slug);
    if (hit && hit.expiresAt > now) return hit.config;
    try {
      const r: any = await this.http.get(
        `${env.apiUrl}/rest/api/brand-config?slug=${encodeURIComponent(slug)}`,
        { withCredentials: false },
      ).toPromise();
      const raw = r?.error_alert_sound;
      const cfg: ErrorAlertSound | null = raw && typeof raw === 'object'
        ? {
            enabled: !!raw.enabled,
            instrument: String(raw.instrument || 'acoustic_grand_piano'),
            note: String(raw.note || 'C5'),
          }
        : null;
      this._cache.set(slug, { config: cfg, expiresAt: now + this.CACHE_TTL_MS });
      return cfg;
    } catch {
      // Preserve nothing — next event retries. Don't burn cache on error.
      return null;
    }
  }

  private _playNote(instrument: string, note: string): void {
    // Volume=0 is functionally muted; skip the entire audio path.
    if (this._volume <= 0) return;
    try {
      if (!this._ctx) {
        const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AC) return; // no Web Audio support
        this._ctx = new AC();
      }
      // After the guard above, _ctx is guaranteed non-null. Bind to a
      // local with explicit non-null so strict TS null checks don't
      // complain on each subsequent usage (new AC() returns `any` and
      // doesn't narrow the field).
      const ctx = this._ctx!;
      // Browser autoplay policy may suspend the context until interaction.
      // The first error post-load might be silent; resume() lets subsequent
      // ones play after the operator clicks anywhere.
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const freq = this._noteToFreq(note);
      const env = this._envelopeFor(instrument);
      // Operator volume scales the envelope peak. 0% → silent (gain 0
      // means no audible output even with the rest of the envelope).
      // Note: at very low volume the exponentialRampToValueAtTime
      // target (0.0001) can exceed the peak; clamp accordingly.
      const peak = Math.max(0.0002, env.peak * (this._volume / 100));
      // Cold-start warm-up: if the AudioContext was just created (or
      // just recreated by the auto-release path), the audio subsystem
      // needs a moment to spin up before it can honor scheduled events.
      // Schedule the FIRST note ~1s into the future so the OS audio
      // session is fully live by the time the oscillator fires — the
      // note lands audibly instead of being silently dropped. Only
      // applies to the cold start; once warm, subsequent notes play
      // immediately. The Web Audio scheduler is sample-accurate over
      // this horizon, so the tone still triggers on time.
      const startAt = this._warm ? ctx.currentTime : ctx.currentTime + this.WARMUP_DELAY_S;
      this._warm = true;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = env.type;
      osc.frequency.setValueAtTime(freq, startAt);
      // ADSR — attack to peak then exponential decay to silence.
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(peak, startAt + env.attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + env.attack + env.release);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + env.attack + env.release + 0.05);
      // Schedule (or extend) the silence-release timer. The 5s starts
      // AFTER the tail of this note has decayed, so short bursts don't
      // trigger a release mid-play. Any subsequent _playNote reschedules
      // the timer forward — five contiguous seconds of silence is what
      // actually closes the context. Cold-start warm-up delay is
      // included so the release doesn't fire before the first note.
      const warmupDelay = startAt - ctx.currentTime;
      const tailEndsAt = warmupDelay + env.attack + env.release + 0.05; // seconds
      this._scheduleRelease(tailEndsAt * 1000 + this.AUTO_RELEASE_MS);
    } catch {
      /* fail-silent — sound is non-essential */
    }
  }

  /**
   * Arm (or re-arm) the auto-release timer. Cancels any pending
   * release before scheduling the new one so a fresh note extends the
   * silence deadline. On fire: close the AudioContext (fully releases
   * the audio session — different from suspend(), which keeps the
   * context alive), null the field, cancel the handle. Next _playNote
   * creates a new context from scratch.
   */
  private _scheduleRelease(delayMs: number): void {
    if (this._releaseTimer) {
      clearTimeout(this._releaseTimer);
      this._releaseTimer = null;
    }
    this._releaseTimer = setTimeout(() => {
      this._releaseTimer = null;
      const ctx = this._ctx;
      if (!ctx) return;
      this._ctx = null;
      // Reset the warm-up flag — the next _playNote is a cold start
      // and needs the 1s spin-up delay again.
      this._warm = false;
      try {
        // Some browsers throw InvalidStateError if the context is already
        // closed (e.g. after page hide). Swallow — the important state
        // (this._ctx = null) is already set.
        void ctx.close();
      } catch { /* ignore */ }
    }, Math.max(0, delayMs));
  }

  private _noteToFreq(note: string): number {
    const semis: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
    const m = /^([A-G])(#|b)?(-?\d)$/.exec(String(note || '').trim());
    if (!m) return 523.25; // C5 default
    const base = semis[m[1]] ?? 0;
    const accidental = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
    const octave = parseInt(m[3], 10);
    // MIDI: A4 = 69 = 440 Hz; midi = (octave+1)*12 + semitoneIndex
    const midi = (octave + 1) * 12 + base + accidental;
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  private _envelopeFor(instrument: string): { type: OscillatorType; attack: number; release: number; peak: number } {
    switch (instrument) {
      case 'tubular_bells':   return { type: 'sine',     attack: 0.005, release: 1.8, peak: 0.30 };
      case 'marimba':         return { type: 'sine',     attack: 0.005, release: 0.40, peak: 0.40 };
      case 'xylophone':       return { type: 'sine',     attack: 0.002, release: 0.25, peak: 0.50 };
      case 'glockenspiel':    return { type: 'triangle', attack: 0.005, release: 1.2,  peak: 0.30 };
      case 'vibraphone':      return { type: 'sine',     attack: 0.030, release: 2.0,  peak: 0.30 };
      case 'celesta':         return { type: 'triangle', attack: 0.005, release: 0.80, peak: 0.40 };
      case 'music_box':       return { type: 'triangle', attack: 0.005, release: 0.60, peak: 0.35 };
      case 'kalimba':         return { type: 'sine',     attack: 0.005, release: 0.80, peak: 0.40 };
      case 'steel_drums':     return { type: 'triangle', attack: 0.010, release: 1.00, peak: 0.40 };
      case 'church_organ':    return { type: 'sawtooth', attack: 0.050, release: 0.40, peak: 0.20 };
      case 'harpsichord':     return { type: 'square',   attack: 0.005, release: 0.35, peak: 0.20 };
      case 'acoustic_grand_piano':
      default:                return { type: 'triangle', attack: 0.005, release: 1.0,  peak: 0.40 };
    }
  }
}

/** Note options for the admin picker dropdown — three octaves of naturals + sharps. */
export const SOUND_NOTES: string[] = (() => {
  const ns: string[] = [];
  const order = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  for (const oct of [3, 4, 5, 6]) for (const n of order) ns.push(`${n}${oct}`);
  return ns;
})();
